/**
 * The shared half of "take a photo": open a picker, shrink what comes back,
 * report it or say why not.
 *
 * Two places need this — the field inside the add sheet and the camera button on
 * the home screen — and they differ only in what they render, so the markup
 * stays with each of them and only the behaviour lives here.
 */

import { useRef, useState } from "react";
import type { ChangeEvent } from "react";

import { downscaleDocument, downscalePhoto } from "../lib/image/downscalePhoto";
import type { PhotoProfile } from "../lib/image/downscalePhoto";

interface PhotoCaptureOptions {
  onPhoto: (photo: string) => void;
  onError: (message: string) => void;
  /**
   * Given instead of downscaling, when the caller needs the original.
   *
   * The receipt flow needs it because it retries at smaller sizes if the first
   * one is refused, and you cannot re-encode a data URL back to a better one.
   * Retry policy belongs with whatever knows what a failure means, which is not
   * this hook.
   */
  onFile?: (file: File) => void;
  /**
   * "thumbnail" for a picture of an object, which gets kept. "document" for a
   * picture of text, which is far too big to keep and is sent once and thrown
   * away. Defaults to the one that is safe to store.
   */
  profile?: PhotoProfile;
}

export function usePhotoCapture({
  onPhoto,
  onError,
  onFile,
  profile = "thumbnail",
}: PhotoCaptureOptions) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isWorking, setIsWorking] = useState(false);

  async function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    // Clearing the input means picking the same file twice still fires onChange.
    event.target.value = "";
    // No file means the picker was dismissed — not an error, just nothing to do.
    if (!file) return;

    setIsWorking(true);
    try {
      if (onFile) {
        onFile(file);
        return;
      }
      // A document is encoded to a size budget rather than to fixed settings,
      // because the same settings produce 123KB for a clean receipt and 392KB
      // for a photograph of one.
      onPhoto(
        profile === "document"
          ? await downscaleDocument(file)
          : await downscalePhoto(file, profile)
      );
    } catch (photoError) {
      onError(
        photoError instanceof Error ? photoError.message : "That photo couldn't be used."
      );
    } finally {
      setIsWorking(false);
    }
  }

  return {
    inputRef,
    isWorking,
    handleChange,
    /** Opens the camera on a phone, the file picker on a desktop. */
    open: () => inputRef.current?.click(),
  };
}
